/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { inject } from "@angular/core";
import { tap } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { EnvironmentService } from "../services/environment.service";
import { openForbiddenDialog } from "../utils/utils";
import { DialogService } from "./../services/dialog.service";

/**
 * HTTP interceptor function that adds custom headers to the request.
 *
 * This interceptor injects the `EnvironmentService` to retrieve the transaction ID,
 * business ID, and session ID, and adds these values as headers to the outgoing request.
 *
 * If the response returns a 401 Unauthorized error, it generates a new session ID using `uuidv4()`
 * and updates the `EnvironmentService` with the new session ID.
 *
 * If the response returns a 403 Forbidden error, it opens a dialog using the `DialogService`.
 *
 * @param req - The outgoing HTTP request.
 * @param next - The next interceptor in the chain.
 * @returns An observable of the HTTP event stream.
 */
export const httpInterceptor: HttpInterceptorFn = (req, next) => {
	const environmentService = inject(EnvironmentService);
	const dialogService = inject(DialogService);

	const transactionId = environmentService.getTransactionId();
	const businessId = environmentService.getBusinessId();
	const sessionId = environmentService.getSessionId();

	const clonedReq = req.clone({
		headers: req.headers
			.set("transaction_id", transactionId)
			.set("business_id", businessId)
			.set("session_id", sessionId)
	});

	return next(clonedReq).pipe(
		tap({
			error: error => {
				if (error instanceof HttpErrorResponse) {
					if (error.status === 401) {
						environmentService.setSessionId(uuidv4());
						environmentService.unauthorizedRedirect();
					} else if (error.status === 403) {
						openForbiddenDialog(dialogService);
					}
				}
			}
		})
	);
};
